import { uiText, useUiLanguage } from '@/i18n/interface';

import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield } from 'lucide-react';
import { FormData } from '../../utils/formValidation';
import { getDisclaimerText } from '../../constants/disclaimerText';
import { useDojoSettings } from '../../hooks/useDojoSettings';

interface DisclaimerStepProps {
  formData: FormData;
  updateFormData: (field: string, value: string | boolean) => void;
}

const DisclaimerStep: React.FC<DisclaimerStepProps> = ({ formData, updateFormData }) => {
  useUiLanguage();
  const { settings } = useDojoSettings();
  const disclaimerText = getDisclaimerText(settings.dojo_name, formData.role);
  const isAdmin = formData.role === 'admin';

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Shield className="h-5 w-5 text-red-900" />
        {isAdmin ? uiText("WarriorHub Administrator Agreement") : uiText("Liability Waiver & Agreement")}
      </h3>

      <Card>
        <CardHeader>
          <CardTitle>
            {isAdmin ? uiText("Administrator Registration Waiver") : uiText("Training Agreement & Waiver of Liability")}
          </CardTitle>
          <CardDescription>
            {isAdmin
              ? uiText("Please read the following administrator agreement carefully before proceeding")
              : uiText("Please read the following disclaimer carefully before signing")
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-64 w-full rounded-md border p-4">
            <div className="whitespace-pre-wrap text-sm leading-relaxed">
              {disclaimerText}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div>
          <Label htmlFor="digitalSignature">{uiText("Digital Signature (Full Legal Name) *")}</Label>
          <Input
            id="digitalSignature"
            value={formData.digitalSignature}
            onChange={(e) => updateFormData('digitalSignature', e.target.value)}
            placeholder={uiText("Type your full legal name")}
            required
          />
        </div>

        <div>
          <Label htmlFor="signatureDate">{uiText("Date *")}</Label>
          <Input
            id="signatureDate"
            type="date"
            value={formData.signatureDate}
            onChange={(e) => updateFormData('signatureDate', e.target.value)}
            required
          />
        </div>

        <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
          <Checkbox
            id="disclaimerAgreed"
            checked={formData.disclaimerAgreed}
            onCheckedChange={(checked) => updateFormData('disclaimerAgreed', checked as boolean)}
          />
          <div className="space-y-1">
            <Label htmlFor="disclaimerAgreed" className="text-sm font-medium">
              {uiText("I have read and agree to the terms *")}</Label>
            <p className="text-sm text-gray-600">
              {isAdmin
                ? uiText("I confirm that I have carefully read the entire WarriorHub Administrator Agreement above, understand all terms and conditions, and voluntarily agree to be bound by this agreement.")
                : uiText("I confirm that I have carefully read the entire liability waiver and training agreement above, understand all terms and conditions, and voluntarily agree to be bound by this agreement.")
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerStep;
